import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';

const testimonials = [
  {
    name: '张先生',
    role: '资深用户·使用2年',
    content: '快猫app是我用过最好用的应用之一，界面简洁，功能强大。智能推荐真的越来越懂我了，每天打开都有惊喜。',
    rating: 5,
  },
  {
    name: '李女士',
    role: '会员用户·使用1年',
    content: '开了会员之后体验更棒了，没有广告真的很清爽。客服响应也特别快，有问题基本几分钟就解决了。',
    rating: 5,
  },
  {
    name: '王同学',
    role: '新用户·使用3个月',
    content: '同学推荐的，下载后发现真的很好用。新手引导做得很友好，很快就上手了，现在每天都离不开。',
    rating: 5,
  },
  {
    name: '赵先生',
    role: '忠实用户·使用3年',
    content: '从早期版本用到现在，见证了快猫app的成长。每次更新都有惊喜，开发团队真的很用心在做产品。',
    rating: 5,
  },
];

const milestones = [
  { year: '2022', title: '项目启动', description: '快猫app项目正式立项，核心团队组建完成' },
  { year: '2023', title: '正式发布', description: 'V1.0版本发布，首批用户突破10万' },
  { year: '2024', title: '快速发展', description: '用户突破100万，推出会员体系' },
  { year: '2025', title: '全面升级', description: 'AI智能推荐上线，用户突破500万' },
  { year: '2026', title: '持续创新', description: 'V3.5发布，多端同步等重大功能更新' },
];

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormState('success');
      form.reset();
      return;
    }

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormState('error');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rg2r4nlsngrm4lmbpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = (parsed && (parsed as Record<string, Record<string, string>>).meta?.message) || responseText;
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormState('error');
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://kuaimaoapp.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://kuaimaoapp.com/about' },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '快猫app',
    url: 'https://kuaimaoapp.com',
    description: '快猫app是一款集智能推荐、多端同步、隐私安全于一体的综合性移动应用，拥有超过500万活跃用户。',
    email: 'support@kuaimaoapp.com',
    telephone: '+86-400-123-4567',
    foundingDate: '2022',
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-24 pb-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">关于我们</span>
          </nav>

          <section className="mb-20">
            <div className="max-w-3xl">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                品牌介绍
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-6">
                关于快猫app
              </h1>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                快猫app诞生于2022年，由一群热爱技术和产品的年轻人共同创建。我们的使命是用科技让每个人的数字生活更加便捷、高效和有趣。经过多年的持续迭代和优化，快猫app已经发展成为拥有超过<strong>500万活跃用户</strong>的综合性移动应用平台。
              </p>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                我们始终坚持以用户体验为中心，不断探索前沿技术。从最初的单一功能到如今的<strong>智能推荐引擎</strong>、<strong>多端数据同步</strong>、<strong>端到端隐私加密</strong>等多元功能矩阵，每一步成长都离不开用户的支持与反馈。
              </p>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                未来，快猫app将继续深耕移动应用领域，探索AI与用户体验的更多可能。我们承诺：<strong>始终保持对产品的热爱，对用户的真诚，对技术的敬畏</strong>。
              </p>
            </div>
          </section>

          <section className="mb-20">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                发展历程
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                我们的成长之路
              </h2>
            </div>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-2 bottom-2 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                      <div className="absolute left-0 md:left-1/2 top-1 w-8 h-8 rounded-full bg-accent-500 border-4 border-background-50 flex items-center justify-center md:-translate-x-1/2">
                        <div className="w-2 h-2 rounded-full bg-background-50"></div>
                      </div>
                      <div className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/70">
                        <span className="text-sm font-bold text-accent-600">{item.year}</span>
                        <h3 className="font-heading text-lg font-semibold text-foreground-900 mt-1 mb-2">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <div className="hidden md:block flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-20">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                用户口碑
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                用户怎么说
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                来自真实用户的评价，是我们前进的最大动力
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="rounded-2xl bg-background-100 border border-background-200/70 p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="contact">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                  有任何问题？随时联系
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  我们的团队会在24小时内回复您的消息
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="text-center p-5 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-1">邮箱</div>
                  <a href="mailto:support@kuaimaoapp.com" className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                    support@kuaimaoapp.com
                  </a>
                </div>
                <div className="text-center p-5 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-phone-line text-accent-600 text-lg"></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-1">电话</div>
                  <a href="tel:4001234567" className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                    400-123-4567
                  </a>
                </div>
                <div className="text-center p-5 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-wechat-line text-secondary-600 text-lg"></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-1">微信</div>
                  <span className="text-sm font-medium text-foreground-900">快猫官方</span>
                </div>
              </div>

              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="rounded-2xl bg-background-100 border border-background-200/70 p-6 md:p-8 space-y-5"
              >
                <input
                  type="text"
                  name="phone_alt"
                  className="honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-2">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">
                      邮箱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-2">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-2">
                    留言内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <div className="text-xs text-foreground-500 mt-1 text-right">最多500个字符</div>
                </div>

                {formState === 'success' && (
                  <div className="p-4 rounded-xl bg-accent-100 text-accent-800 text-sm font-medium">
                    <i className="ri-check-line mr-2"></i>
                    提交成功！感谢您的留言，我们会在24小时内回复。
                  </div>
                )}
                {formState === 'error' && (
                  <div className="p-4 rounded-xl bg-red-50 text-red-700 text-sm font-medium">
                    <i className="ri-error-warning-line mr-2"></i>
                    {formError || '提交失败，请稍后重试'}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-3.5 rounded-xl bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-line mr-2"></i>
                  发送留言
                </button>
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ScrollToTopButton />
    </>
  );
}